/**
 * The pure half of the Alt Data access rule (see src/hooks/useAltDataAccess.ts for the contract),
 * kept free of the store so unit tests can import it.
 */

export interface AltDataUserData {
    alt_data?: boolean;
}

/**
 * Does this user have the Alt Data entitlement?
 *
 * Returns `undefined` until the `/API/v2/user` call has settled (`userPayloadSettledState`), so
 * callers can hold rendering rather than flash a section the user may not be allowed to see. The
 * user object alone cannot say that: a settings write can leave a partial object in the store
 * before the payload lands. Every settled case returns a boolean; settled with no user is "no".
 */
export function hasAltDataAccess(userData: AltDataUserData | null | undefined, settled: boolean): boolean | undefined {
    if (!settled) return undefined;
    // Present-but-false and absent-entirely both mean "no". Absent covers a
    // backend older than the flag, and treating it as no is the fail-closed
    // half of the contract in src/hooks/useAltDataAccess.ts.
    return !!userData?.alt_data;
}

/**
 * What an Alt Data page shows:
 * - `pending`: entitlement not known yet (the server render, which is static and built without
 *   a user, and the first client render before the user payload arrives). The page's text and a
 *   locked preview, no upgrade prompt, no data.
 * - `locked`: a definite no. The same, plus the upgrade prompt.
 * - `full`: entitled. The real page, exactly as before.
 */
export type AltDataView = "pending" | "locked" | "full";

export function altDataView(userData: AltDataUserData | null | undefined, settled: boolean): AltDataView {
    const access = hasAltDataAccess(userData, settled);
    return access === undefined ? "pending" : access ? "full" : "locked";
}
